/* prettier-ignore */

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, signal } from '@angular/core';
import { MarkdownModule } from 'ngx-markdown';
import { Message } from 'src/chatbot/interface/message.interface';
import { DataChatService } from 'src/chatbot/service/dataChat.service';
import { ClipBoardIconComponent } from 'src/chatbot/icons/clipboard-icon.component';
import { SoundIconComponent } from 'src/chatbot/icons/sound-icon.component';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { StopMusicIconComponent } from 'src/chatbot/icons/stop-icon.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DebugService } from 'src/app/services/debug.service';
import { AudioStorageService } from 'src/chatbot/service/audio-storage.service';
import { LinkInterceptorDirective } from 'src/app/directives/link/link.directive';
import { PlayAudioHandlerService } from 'src/chatbot/service/playAudioHandler.service';
import { environment } from 'src/environments/environment';
import { LinkPreviewComponent } from '../linkPreview/link-preview.component';
import { WeatherWidgetComponent } from '../weatherWidget/weatherWidget.component';
import { SendMessageService } from '../../service/sendMessage.service';

@Component({
  selector: 'chat-bubble',
  standalone: true,
  imports: [
    CommonModule,
    MarkdownModule,
    ClipBoardIconComponent,
    SoundIconComponent,
    MatButtonModule,
    MatTooltipModule,
    LinkInterceptorDirective,
    StopMusicIconComponent,
    TranslateModule,
    LinkPreviewComponent,
    WeatherWidgetComponent
  ],
  templateUrl: './chatBubble.component.html',
  styleUrls: ['./chatBubble.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ChatBubbleComponent {
  private debugService = inject(DebugService);
  private playAudioHandlerService = inject(PlayAudioHandlerService);
  private audioStorageService = inject(AudioStorageService);
  public dataChat = inject(DataChatService);
  private sendMessageService = inject(SendMessageService);
  public translate = inject(TranslateService);

  public isStreaming = computed(() => this.dataChat.isStreaming());
  public isPlayingOnDemand = computed(() => this.playAudioHandlerService.isPlayingOnDemand());
  public isPlayingChatbot = computed(() => this.playAudioHandlerService.isPlaying());
  public isLoadingAudioById = signal(null);

  public messages = computed(() => this.dataChat.messages());  
  public initialMessage = computed(() =>  this.dataChat.initialMessageCurrentLang());
  private timeout: any;
  
  constructor() { }

  isUser(role: string) {
    return role === 'user';
  }

  /** Extrae URLs externas del contenido del mensaje */
  extractUrls(content: string): string[] {
    if (!content) 
      return [];

    const urlRegex = /(?:https?:\/\/|www\.)[^\s\)\]"'<>]+|(?:[a-zA-Z0-9-]+\.)+(?:com|es|org|net|io|info|gov|edu|co|eu|uk|de|fr|it)[^\s\)\]"'<>]*/gi;
    const matches = content.match(urlRegex) || [];

    // Normalizar URLs y filtrar
    const externalUrls = matches
      .map(url => {
        url = url.replace(/[.,;:!?]+$/, '');
        if (!/^https?:\/\//i.test(url)) 
          return 'https://' + url;
        
        return url;
      })
      .filter(url => {
        try {
          const parsed = new URL(url);
          if (parsed.hostname.length < 4) 
            return false;

          return parsed.origin !== window.location.origin;
        } catch {
          return false;
        }
      });

    return [...new Set(externalUrls)];
  }

  copyToClipboard(event: any) {
    const text = event.target.closest('.chat__message-wrapper')?.querySelector('.markdown').innerText;
    if (!text)
      return;

    const tooltip = event.target.closest('.action-icons-container')?.querySelector('.copy-text');
    if (tooltip) {
      tooltip.classList.add('tooltip-show');
      this.timeout = setTimeout(() => {
        tooltip.classList.remove('tooltip-show');
      }, 1000);
    }

    navigator.clipboard.writeText(text).catch((err) => {
      this.debugService.error(err, '[ChatBubbleComponent] Error copying text');
    });
  }

  removeEmojis(text: string): string {
    return text.replace(
      /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu,
      ''
    );
  }

  async playAudio(event, errAudioElm) {
    if (this.isPlayingChatbot())
      return;

    this.playAudioHandlerService.prepareOnDemandFromGesture();

    const target = event.currentTarget as HTMLElement;
    const completion_id = target.getAttribute('data-tracking-id');
    const audioBlob = await this.audioStorageService.getAudio(completion_id);
    if (!audioBlob) {
      const text = event.target.closest('.chat__message-wrapper')?.querySelector('.markdown').innerText;
      const textWithoutEmojis = this.removeEmojis(text)?.replaceAll('-', ' ');
      const audioObject = { id: completion_id, text: textWithoutEmojis };
      this.getAudioFromText(audioObject, errAudioElm);
      return;
    }

    const audioObject = { completion_id, audioBlob };
    this.playAudioHandlerService.playAudioOnDemand(audioObject);
  }

  stopAudio() {
    this.playAudioHandlerService.stopAudioOnDemand();
  }

  checkPlayingOnDemand(id) {
    return this.isPlayingOnDemand() === id;
  }

  getAudioFromText(audioObject: any, target: any) {
    const { id, text } = audioObject;
    if (!text || text === "") {
      console.warn("No hay texto para reproducir");
      return;
    }

    this.playFromAPI(id, text, target);
  }

  async playFromAPI(id: any, text: string, target: any) {
    this.isLoadingAudioById.set(id);
    const url = `${environment.apiAssistantUrlBase}/tts/simple?text=${encodeURIComponent(text)}&t=${Date.now()}`;

    try {
      const res = await fetch(url);
      if (!res.ok) 
        throw new Error(`TTS HTTP ${res.status}`);

      const blob = await res.blob();
      const contentType = res.headers.get('content-type') || blob.type || 'audio/mpeg';
      const audioBlob = blob.type ? blob : new Blob([blob], { type: contentType });

      await this.audioStorageService.storeAudio(id, audioBlob);
      this.playAudioHandlerService.playAudioOnDemand({
        completion_id: id,
        audioBlob: blob,
      });

    } catch (err) {
      this.showErrorAudioTooltip(target);
      console.warn('⚠️ [LocalFiller] Error playing audio filler:', err);
    } finally {
      this.isLoadingAudioById.set(null);
    }
  }

  sendFeedback(message: Message, feedback: 'positive' | 'negative') {
    if (!message.id || message.feedback === feedback) 
      return;

    const feedbackData = {
      conversation_id: this.dataChat.token() || 'unknown',
      service_id: parseInt(this.dataChat.id()),
      message_id: message.id,
      feedback: feedback
    };

    const apiUrl = `${environment.apiAssistantUrlBase}/metrics/feedback`;
    fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(feedbackData)
    }).then(res => res.json())
      .then(data => {
        this.debugService.info(data, '[ChatBubbleComponent] Feedback saved');
        this.setFeedBack(message.id, feedback)
      })
      .catch(err => {
        this.debugService.error(err, '[ChatBubbleComponent] Error saving feedback');
      }
    );
  }

  private setFeedBack(id, feedback){
    this.dataChat.messages.update(prev => prev.map(msg => (msg.id === id ? {...msg, feedback} : msg)));
  }

  private showErrorAudioTooltip(target: any) {
    const tooltip = target.closest('.action-icons-container')?.querySelector('.error-audio');
    if (tooltip) {
      tooltip.classList.add('tooltip-show');
      this.timeout = setTimeout(() => {
        tooltip.classList.remove('tooltip-show');
      }, 1000);
    }
  }

  onProactiveClick(message: Message) {
    if (this.isStreaming()) 
      return;

    const commandToSend = message.hiddenCommand || message.content.replace('🖱️', '').trim();
    this.sendMessageService.sendMessageWebSocket(commandToSend, true);
  }

}
